import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Bell, Check } from 'lucide-react'
import {
  acknowledgeNotification,
  fetchNotificationInboxRaw,
  markAllNotificationsRead,
  markNotificationRead
} from '@/store/actions/notificationAction'

/**
 * W10b (NOT-001) · The in-app notification bell: unread badge, latest
 * entries, mark-read. Polls the inbox every 60s; a click-through marks the
 * entry read. Settings live at /account/notifications.
 */
const SEVERITY_DOT = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-slate-400'
}

const timeAgo = (value) => {
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

const NotificationBell = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [notifications, setNotifications] = useState([])
  const panelRef = useRef(null)

  const refresh = useCallback(async () => {
    try {
      const res = await dispatch(fetchNotificationInboxRaw())
      setUnread(res.data?.data?.unread || 0)
      setNotifications(res.data?.data?.notifications || [])
    } catch (error) { /* the bell must never break the shell */ }
  }, [dispatch])

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 60000)
    return () => clearInterval(interval)
  }, [refresh])

  useEffect(() => {
    if (!open) return undefined
    const onClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const markRead = async (notification) => {
    if (notification.readAt) return
    try {
      await dispatch(markNotificationRead(notification._id))
      refresh()
    } catch (error) { /* ignore */ }
  }

  /**
   * NOT-005 · acknowledgement. Marking read happens when a panel renders and
   * proves nothing; acknowledging is this person saying they have seen it
   * and taken it on, and that is the version worth putting in an evidence
   * pack. The first acknowledgement stands — the server refuses to
   * overwrite the moment somebody actually did it.
   */
  const acknowledge = async (event, notification) => {
    event.stopPropagation()
    try {
      await dispatch(acknowledgeNotification(notification._id))
      refresh()
    } catch (error) { /* the bell must never break the shell */ }
  }

  const markAll = async () => {
    try {
      await dispatch(markAllNotificationsRead())
      refresh()
    } catch (error) { /* ignore */ }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        className="relative flex h-8 w-8 items-center justify-center rounded-md hover:bg-black/5"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Bell size={18} className="text-slate-700" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] rounded-full bg-red-500 px-1 text-center text-[10px] font-semibold leading-4 text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-96 rounded-md border border-borderV1 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-borderV1 px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button type="button" className="text-xs text-themeV2 hover:underline" onClick={markAll}>Mark all read</button>
              )}
              <Link to="/account/notifications" className="text-xs text-gray-500 hover:underline" onClick={() => setOpen(false)}>Settings</Link>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 && <p className="py-8 text-center text-sm text-gray-400">Nothing yet.</p>}
            {notifications.map((notification) => (
              <div
                key={notification._id}
                className={`border-b border-borderV1/50 px-3 py-2 hover:bg-slate-50 ${notification.readAt ? 'opacity-60' : ''}`}
              >
                <button
                  type="button"
                  className="block w-full text-left"
                  onClick={() => markRead(notification)}
                >
                  <span className="flex items-start gap-2">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[notification.severity] || SEVERITY_DOT.info}`} />
                    <span className="flex-1">
                      <span className="block text-sm text-slate-800">{notification.title}</span>
                      {notification.body && <span className="block text-xs text-gray-500">{notification.body}</span>}
                      <span className="block text-[11px] text-gray-400">{timeAgo(notification.createdAt)}</span>
                    </span>
                  </span>
                </button>
                {/* Sibling of the mark-read button, never inside it: one
                    control per act, and acknowledging is a different act. */}
                <div className="pl-4">
                  {notification.acknowledgedAt ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                      <Check size={11} /> Acknowledged
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-[11px] text-themeV2 hover:underline"
                      onClick={(event) => acknowledge(event, notification)}
                    >
                      Acknowledge
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
